import React, { useState, useEffect } from 'react';
import { getAuthHeaders } from '../utils/authHeaders';
import CompanyAdminPicker from '../components/CompanyAdminPicker';
import { API, btnPrimary, inputStyle, FormRow } from './RpShared';

export default function RpSettings() {
  const companyId = localStorage.getItem('selectedCompanyId');
  const [settings, setSettings] = useState(null);
  const [users, setUsers] = useState([]);
  const [adminIds, setAdminIds] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => {
    const controller = new AbortController();
    setSettings(null); setError('');
    const get = async path => {
      const res = await fetch(API + path, { headers: getAuthHeaders(), signal: controller.signal });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Unable to load settings.');
      return data;
    };
    Promise.all([get('/companies/' + companyId + '/roles-permissions/settings'), get('/users')]).then(([data, list]) => {
      if (controller.signal.aborted) return;
      setSettings(data); setUsers(list); setAdminIds(data.administrators.map(user => Number(user.id)));
    }).catch(e => { if (!controller.signal.aborted) setError(e.message); });
    return () => controller.abort();
  }, [companyId]);

  const save = async () => {
    setSaving(true); setError(''); setMessage('');
    try {
      const payload = {
        require_approval_for_sensitive: settings.require_approval_for_sensitive,
        self_lockout_protection: settings.self_lockout_protection,
        default_temp_duration_days: settings.default_temp_duration_days,
      };
      if (settings.can_manage_privacy) Object.assign(payload, { isolate_from_holding: settings.isolate_from_holding, admin_ids: adminIds });
      const res = await fetch(API + '/companies/' + companyId + '/roles-permissions/settings', {
        method: 'PUT', headers: getAuthHeaders({ 'Content-Type': 'application/json' }), body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(Object.values(data.errors || {}).flat().join(' ') || data.message || 'Unable to save settings.');
      setSettings(data); setAdminIds(data.administrators.map(user => Number(user.id))); setMessage('Company access settings saved.');
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  return <div style={{ padding: '24px 32px', maxWidth: 760 }}>
    <h2>Company administrators & privacy</h2>
    {error && <p role="alert" style={{ color: '#b91c1c' }}>{error}</p>}
    {message && <p role="status" style={{ color: '#047857' }}>{message}</p>}
    {!settings ? !error && <p>Loading settings…</p> : <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: 24 }}>
      <CompanyAdminPicker users={users} selected={adminIds} onChange={setAdminIds} disabled={!settings.can_manage_privacy || saving} />
      <FormRow label="Private company">
        <label><input type="checkbox" role="switch" checked={!!settings.isolate_from_holding} disabled={!settings.can_manage_privacy || saving} onChange={e => setSettings({ ...settings, isolate_from_holding: e.target.checked })} /> Make this company private</label>
        <p style={{ color: '#475569', fontSize: 13 }}>Private companies are excluded from holding dashboards and totals. Holding administrators and superadmins cannot access their data unless explicitly assigned as company administrators. Company users keep their assigned access.</p>
        <p style={{ color: '#475569', fontSize: 13 }}>Only a company administrator can switch privacy off and restore holding access.</p>
        {!settings.can_manage_privacy && <p>Ask an assigned company administrator to change administrators or privacy.</p>}
      </FormRow>
      <FormRow label="Sensitive Permission Approval"><label><input type="checkbox" checked={!!settings.require_approval_for_sensitive} onChange={e => setSettings({ ...settings, require_approval_for_sensitive: e.target.checked })} /> Require approval for sensitive permission grants</label></FormRow>
      <FormRow label="Self-Lockout Protection"><label><input type="checkbox" checked={!!settings.self_lockout_protection} onChange={e => setSettings({ ...settings, self_lockout_protection: e.target.checked })} /> Protect administrative access</label><p style={{ fontSize: 13 }}>At least one company administrator is always required.</p></FormRow>
      <FormRow label="Default Temporary Grant Duration (days)"><input type="number" min={1} max={365} style={inputStyle} value={settings.default_temp_duration_days || 7} onChange={e => setSettings({ ...settings, default_temp_duration_days: Number(e.target.value) })} /></FormRow>
      <button style={btnPrimary} onClick={save} disabled={saving || (settings.can_manage_privacy && !adminIds.length)}>{saving ? 'Saving…' : 'Save Settings'}</button>
    </div>}
  </div>;
}
